import { lazy, Suspense } from 'react'
import { Navigate } from 'react-router'

import { useUserQuery } from '@/hooks/user/use-user-query'
import { preloadNamespaces, withNamespaces } from '@/i18n/route-namespaces'
import { useFeatureFlags } from '@/stores/use-feature-flags'

import { RouteFallback } from '../route-fallback'

const BotThemeBase = lazy(async () => {
  await preloadNamespaces(['botTheme'])

  const { BotThemeBase: Component } = await import('@/pages/bot-theme')

  return { default: Component }
})

function BotThemeRouteGuard() {
  const { featureFlags, isLoaded } = useFeatureFlags()
  const { data: user } = useUserQuery()

  if (!isLoaded) {
    return <RouteFallback />
  }

  const isUserRootOrAdmin = user.is_root || user.is_admin

  if (!featureFlags.botTheme || !isUserRootOrAdmin) {
    return <Navigate to="/companies" replace />
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <BotThemeBase />
    </Suspense>
  )
}

export const settingsRoutes = {
  path: 'settings',
  children: [
    {
      index: true,
      element: <Navigate to="integrations" />,
    },
    {
      path: 'integrations',
      lazy: withNamespaces(['integrations'], async () => {
        const { IntegrationsBase } = await import('@/pages/integrations')

        return { Component: IntegrationsBase }
      }),
      children: [
        {
          index: true,
          element: <Navigate to="list" />,
        },
        {
          path: 'list',
          lazy: withNamespaces(['integrations'], async () => {
            const { IntegrationsList } = await import(
              '@/pages/integrations/list'
            )

            return { Component: IntegrationsList }
          }),
        },
      ],
    },
    {
      path: 'migrations',
      lazy: withNamespaces(['botMigration'], async () => {
        const { MigrationsBase } = await import('@/pages/bot-migrations')

        return { Component: MigrationsBase }
      }),
      children: [
        {
          index: true,
          element: <Navigate to="list" />,
        },
        {
          path: 'list',
          lazy: withNamespaces(['botMigration', 'bubble'], async () => {
            const { MigrationsList } = await import(
              '@/pages/bot-migrations/list'
            )

            return { Component: MigrationsList }
          }),
        },
      ],
    },
    {
      path: 'theme',
      element: <BotThemeRouteGuard />,
      children: [
        {
          index: true,
          element: <Navigate to="appearance" />,
        },
        {
          path: 'appearance',
          lazy: withNamespaces(['botTheme'], async () => {
            const { BotThemeAppearance } = await import(
              '@/pages/bot-theme/appearance'
            )

            return { Component: BotThemeAppearance }
          }),
        },
      ],
    },
  ],
}
